import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, SignOut, DeviceMobile, BellRinging } from "@phosphor-icons/react";

function BusinessHoursCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/telephony/settings").then(({ data }) => setSt(data.settings)).catch(() => {}); }, []);
  if (!st || !st.pbx_entitled) return null;
  const upd = (k, v) => setSt((p) => ({ ...p, [k]: v }));
  const days = st.bh_days || [0, 1, 2, 3, 4, 5];
  const toggleDay = (d) => { const has = days.includes(d); upd("bh_days", has ? days.filter((x) => x !== d) : [...days, d].sort()); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/telephony/settings", {
        bh_enabled: !!st.bh_enabled, bh_timezone: st.bh_timezone || "America/New_York",
        bh_days: st.bh_days || [0, 1, 2, 3, 4, 5], bh_open: st.bh_open || "08:00", bh_close: st.bh_close || "20:00",
        after_hours_greeting: st.after_hours_greeting || "", ai_receptionist_enabled: !!st.ai_receptionist_enabled,
      });
      toast.success("Business hours saved");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const DAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
  const TZS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Africa/Douala", "Europe/London", "UTC"];
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="business-hours-section">
      <h2 className="font-display text-lg font-semibold">Business hours & after-hours</h2>
      <p className="mt-1 text-sm text-muted-foreground">Set when your line is open. Outside these hours, callers hear your after-hours greeting, then the SI receptionist (if on) or voicemail.</p>
      <label className="mt-3 flex items-center gap-2 text-sm"><input data-testid="bh-enabled" type="checkbox" checked={!!st.bh_enabled} onChange={(e) => upd("bh_enabled", e.target.checked)} className="h-4 w-4" /> Enforce business hours</label>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div><p className="text-xs font-medium text-muted-foreground">Timezone</p>
          <select data-testid="bh-timezone" value={st.bh_timezone || "America/New_York"} onChange={(e) => upd("bh_timezone", e.target.value)} className="mt-1 w-full rounded-xl border border-input bg-background px-2 py-2 text-sm">
            {TZS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select></div>
        <div><p className="text-xs font-medium text-muted-foreground">Open</p>
          <input data-testid="bh-open" type="time" value={st.bh_open || "08:00"} onChange={(e) => upd("bh_open", e.target.value)} className="mt-1 w-full rounded-xl border border-input bg-background px-2 py-2 text-sm" /></div>
        <div><p className="text-xs font-medium text-muted-foreground">Close</p>
          <input data-testid="bh-close" type="time" value={st.bh_close || "20:00"} onChange={(e) => upd("bh_close", e.target.value)} className="mt-1 w-full rounded-xl border border-input bg-background px-2 py-2 text-sm" /></div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {DAYS.map(([label, d]) => (
          <button key={d} data-testid={`bh-day-${d}`} onClick={() => toggleDay(d)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${days.includes(d) ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>{label}</button>
        ))}
      </div>
      <label className="mt-4 block text-xs font-medium text-muted-foreground">After-hours greeting</label>
      <textarea data-testid="bh-greeting" rows={3} value={st.after_hours_greeting || ""} onChange={(e) => upd("after_hours_greeting", e.target.value)} className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
      <label className="mt-3 flex items-center gap-2 text-sm"><input data-testid="bh-ai" type="checkbox" checked={!!st.ai_receptionist_enabled} onChange={(e) => upd("ai_receptionist_enabled", e.target.checked)} className="h-4 w-4" /> Use SI receptionist after hours (falls back to voicemail)</label>
      <button data-testid="bh-save" onClick={save} disabled={busy} className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Saving…" : "Save business hours"}</button>
    </div>
  );
}

function PhoneCard({ user, refresh }) {
  const [stage, setStage] = useState("view"); // view | enter | otp
  const [phone, setPhone] = useState("");
  const [cid, setCid] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const verified = !!user?.phone_verified;

  const start = async () => {
    if (!phone.trim()) { toast.error("Enter your mobile number"); return; }
    setBusy(true);
    try { const { data } = await api.post("/account/phone/start", { phone: phone.trim() }); setCid(data.challenge_id); setStage("otp"); toast.success("We texted you a verification code"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const verify = async () => {
    if (!/^\d+$/.test(code.trim())) { toast.error("Enter the verification code"); return; }
    setBusy(true);
    try { await api.post("/account/phone/verify", { challenge_id: cid, code: code.trim() }); toast.success("Mobile number verified"); setStage("view"); setPhone(""); setCode(""); await refresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="profile-phone-card">
      <div className="mb-3 flex items-center gap-3">
        <DeviceMobile size={20} className="text-primary" weight="duotone" />
        <div>
          <h2 className="font-display text-lg font-semibold">Mobile number</h2>
          <p className="text-xs text-muted-foreground">Your verified number secures your account and enables SMS sign-in &amp; alerts.</p>
        </div>
      </div>

      {stage === "view" && (
        verified ? (
          <div className="flex flex-wrap items-center gap-3" data-testid="profile-phone-verified">
            <span className="font-mono-metric text-sm">{user.notify_phone_masked}</span>
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">Verified</span>
            <button data-testid="profile-phone-change" onClick={() => setStage("enter")} className="ml-auto rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:border-primary">Change number</button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3" data-testid="profile-phone-unverified">
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">No verified number</span>
            <button data-testid="profile-phone-change" onClick={() => setStage("enter")} className="ml-auto rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground">Add &amp; verify</button>
          </div>
        )
      )}

      {stage === "enter" && (
        <div className="flex gap-2">
          <input data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+12025550123"
            className="flex-1 rounded-xl border border-input bg-card px-4 py-3 font-mono-metric text-sm outline-none focus:border-primary" />
          <button data-testid="profile-phone-send" onClick={start} disabled={busy}
            className="rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Sending…" : "Send code"}</button>
          <button onClick={() => setStage("view")} className="rounded-full border border-border px-4 py-3 text-sm">Cancel</button>
        </div>
      )}

      {stage === "otp" && (
        <div>
          <p className="mb-2 text-sm text-muted-foreground">Enter the verification code we texted to <span className="font-semibold text-foreground">{phone}</span>.</p>
          <div className="flex gap-2">
            <input data-testid="profile-phone-otp" inputMode="numeric" maxLength={10} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="flex-1 rounded-xl border border-input bg-card px-4 py-3 text-center font-mono-metric tracking-[0.4em] text-sm outline-none focus:border-primary" placeholder="••••••" />
            <button data-testid="profile-phone-verify" onClick={verify} disabled={busy}
              className="rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Verifying…" : "Verify"}</button>
          </div>
          <button onClick={() => setStage("enter")} className="mt-2 text-xs text-muted-foreground hover:text-foreground">← Use a different number</button>
        </div>
      )}
    </div>
  );
}

function MarketingCard() {
  const [optedIn, setOptedIn] = useState(true);
  const [wa, setWa] = useState(false);
  const [hasPhone, setHasPhone] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/ai-sales/marketing")
      .then(({ data }) => { setOptedIn(!!data.opted_in); setWa(!!data.whatsapp); setHasPhone(!!data.has_phone); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);
  const toggle = async (e) => {
    const on = e.target.checked;
    setOptedIn(on); setBusy(true);
    try {
      await api.post("/ai-sales/marketing", { opted_in: on });
      toast.success(on ? "You'll receive iYeneh news & offers" : "You've opted out of marketing");
    } catch (err) { setOptedIn(!on); toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const toggleWa = async (e) => {
    const on = e.target.checked;
    setWa(on); setBusy(true);
    try {
      await api.post("/ai-sales/marketing", { whatsapp: on });
      toast.success(on ? "You'll get iYeneh updates on WhatsApp" : "WhatsApp updates turned off");
    } catch (err) { setWa(!on); toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="marketing-optin-section">
      <h2 className="mb-3 font-display text-lg font-semibold">Communication preferences</h2>
      <label className="flex items-start gap-3 text-sm">
        <input data-testid="marketing-optin-checkbox" type="checkbox" checked={optedIn}
          disabled={busy || !loaded} onChange={toggle} className="mt-0.5 h-4 w-4" />
        <span>
          <span className="font-medium">Accept marketing &amp; product news from iYeneh</span>
          <br />
          <span className="text-xs text-muted-foreground">Get offers, tips and updates about iYeneh services. You can opt out anytime by unchecking this box.</span>
        </span>
      </label>
      <label className="mt-4 flex items-start gap-3 text-sm">
        <input data-testid="marketing-whatsapp-checkbox" type="checkbox" checked={wa}
          disabled={busy || !loaded} onChange={toggleWa} className="mt-0.5 h-4 w-4" />
        <span>
          <span className="font-medium">Also reach me on WhatsApp</span>
          <br />
          <span className="text-xs text-muted-foreground">
            {hasPhone ? "Get offers and updates on WhatsApp using your verified mobile number."
                      : "Add a verified mobile number to enable WhatsApp updates."}
          </span>
        </span>
      </label>
    </div>
  );
}

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const brand = useBrand();

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Profile & Settings</h1>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-2xl font-semibold text-primary-foreground">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-display text-xl font-semibold">{user?.name}</p>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <span className="mt-1 inline-block rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-foreground">{user?.role}</span>
          </div>
        </div>
      </div>

      <PhoneCard user={user} refresh={refresh} />

      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="mb-3 font-display text-lg font-semibold">Security</h2>
        <div className="flex items-center justify-between rounded-xl bg-secondary p-4">
          <div className="flex items-center gap-3"><ShieldCheck size={20} className="text-primary" weight="duotone" /><div><p className="text-sm font-medium">Multi-Factor Authentication</p><p className="text-xs text-muted-foreground">MFA-ready · configure in a future release</p></div></div>
          <span className="rounded-full bg-card px-3 py-1 text-xs font-semibold">{user?.mfa_enabled ? "On" : "Off"}</span>
        </div>
      </div>

      <MarketingCard />

      <BusinessHoursCard />

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-3"><DeviceMobile size={20} className="text-primary" weight="duotone" /><div><p className="text-sm font-medium">Install the app</p><p className="text-xs text-muted-foreground">Use your browser's "Add to Home Screen" / "Install" for a full-screen PWA.</p></div></div>
      </div>

      <button data-testid="profile-logout" onClick={logout}
        className="flex w-full items-center justify-center gap-2 rounded-full border border-destructive/30 py-3.5 font-semibold text-destructive transition-colors hover:bg-destructive/5">
        <SignOut size={18} /> Sign out
      </button>
      <p className="text-center text-xs text-muted-foreground">{brand.brand_name} · {brand.tagline}</p>
    </div>
  );
}
